"use client";
import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useCallback, useState, useTransition } from "react";

type ServerAction<T> = (state: T, formData: FormData) => Promise<T>;
type FormSubmitHandler = (e: FormEvent<HTMLFormElement>) => void;

/**
 * This hook is used to handle form submission with a server action
 * that does not reset the form state.
 * Unlike `useActionState`, this hook is to be used with form `onSubmit` prop.
 *
 * @param serverAction - The server action to be called on form submission.
 * @param initialState - The initial state of the form.
 *
 * @returns Array containing form state, the submission handler and pending indicator.
 */
export function useActionStateWithoutReset<T>(
  serverAction: ServerAction<T>,
  initialState: T
): [T, FormSubmitHandler, boolean, Dispatch<SetStateAction<T>>] {
  const [formState, setFormState] = useState<T>(initialState);
  const [pending, startTransition] = useTransition();

  const handleSubmit = useCallback<FormSubmitHandler>(
    (event) => {
      event.preventDefault();
      startTransition(async () => {
        const response = await serverAction(
          formState,
          new FormData(event.currentTarget)
        );
        setFormState(response);
      });
    },
    [formState, serverAction]
  );

  return [formState, handleSubmit, pending, setFormState];
}
